import React, { useState, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Play, Save, CheckCircle2, Info } from 'lucide-react';
import { createPortal } from 'react-dom';

import patientCallingService , {PatientServiceLog } from '../../../../services/patientCalling.service';
import Step1CallType, { CallType } from './Step1CallType';
import Step2RecordSelection from './Step2RecordSelection';
import Step3ScheduleConfig, { ScheduleFormState } from './Step3ScheduleConfig';
import styles from './ScheduleCampaignFlow.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
    onClose: () => void;
    onScheduled: () => void;
}

type Step = 1 | 2 | 3;

// ─── Step metadata ────────────────────────────────────────────────────────────

const STEPS: { step: Step; label: string; short: string }[] = [
    { step: 1, label: 'Call Type',           short: 'Call Type' },
    { step: 2, label: 'Select Patients',     short: 'Patients' },
    { step: 3, label: 'Schedule & Config',   short: 'Schedule' },
];

const CALL_TYPE_LABELS: Record<string, string> = {
    payment_reminder: 'Payment Reminder',
    patient_info_gathering:   'Patient Info Gathering',
};

function defaultScheduleState(callType: CallType): ScheduleFormState {
    return {
        calling_system_call_type: CALL_TYPE_LABELS[callType] ?? callType,
        calling_system_entity_type: 'Patient',
        schedule_type: 'one_time',
        scheduled_at: '',
        cron_expression: '',
        extra_parameters: [],
    };
}

// ─── Validation ───────────────────────────────────────────────────────────────

function validateStep3(form: ScheduleFormState): Partial<Record<keyof ScheduleFormState, string>> {
    const errors: Partial<Record<keyof ScheduleFormState, string>> = {};
    if (!form.calling_system_entity_type) {
        errors.calling_system_entity_type = 'Entity type is required';
    }
    if (form.schedule_type === 'one_time' && !form.scheduled_at) {
        errors.scheduled_at = 'Date and time is required for one-time schedules';
    }
    if (form.schedule_type === 'recurring' && !form.cron_expression.trim()) {
        errors.cron_expression = 'Cron expression is required for recurring schedules';
    }
    return errors;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ScheduleCampaignFlow: React.FC<Props> = ({ onClose, onScheduled }) => {
    const [currentStep, setCurrentStep] = useState<Step>(1);

    // Step 1
    const [selectedCallType, setSelectedCallType] = useState<CallType | null>(null);

    // Step 2
    const [selectedRecords, setSelectedRecords] = useState<PatientServiceLog[]>([]);

    // Step 3
    const [scheduleForm, setScheduleForm] = useState<ScheduleFormState | null>(null);
    const [step3Errors, setStep3Errors] = useState<Partial<Record<keyof ScheduleFormState, string>>>({});

    // Submit state
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // ── Navigation ────────────────────────────────────────────────────────────

    const canProceedStep1 = selectedCallType !== null;
    const canProceedStep2 = selectedRecords.length > 0;

    const goNext = () => {
        if (currentStep === 1 && canProceedStep1) {
            // Initialize schedule form when entering step 3 (on first entry)
            if (!scheduleForm || scheduleForm.calling_system_call_type !== CALL_TYPE_LABELS[selectedCallType!]) {
                setScheduleForm(defaultScheduleState(selectedCallType!));
            }
            setCurrentStep(2);
        } else if (currentStep === 2 && canProceedStep2) {
            if (!scheduleForm) {
                setScheduleForm(defaultScheduleState(selectedCallType!));
            }
            setCurrentStep(3);
        }
    };

    const goBack = () => {
        if (currentStep > 1) setCurrentStep((currentStep - 1) as Step);
    };

    const handleCallTypeChange = (ct: CallType) => {
        setSelectedCallType(ct);
        // Reset downstream state if call type changed
        setSelectedRecords([]);
        setScheduleForm(null);
        setStep3Errors({});
    };

    // ── Build payload ─────────────────────────────────────────────────────────

    const buildPayload = useCallback(() => {
        if (!scheduleForm || !selectedCallType) return null;

        const extraParams = scheduleForm.extra_parameters
            .filter(p => p.key.trim())
            .reduce<Record<string, string>>((acc, p) => {
                acc[p.key.trim()] = p.value;
                return acc;
            }, {});

        return {
            calling_system_call_type: scheduleForm.calling_system_call_type,
            calling_system_entity_type: scheduleForm.calling_system_entity_type,
            schedule_type: scheduleForm.schedule_type,
            ...(scheduleForm.schedule_type === 'one_time'
                ? { scheduled_at: scheduleForm.scheduled_at }
                : { cron_expression: scheduleForm.cron_expression }
            ),
            ...(Object.keys(extraParams).length > 0 ? { extra_parameters: extraParams } : {}),
            service_log_ids: selectedRecords.map(r => r.id),
        };
    }, [scheduleForm, selectedCallType, selectedRecords]);

    // ── Submit handlers ───────────────────────────────────────────────────────

    const validateAndSubmit = useCallback(async (trigger: boolean) => {
        if (!scheduleForm) return;

        const errs = validateStep3(scheduleForm);
        if (Object.keys(errs).length > 0) {
            setStep3Errors(errs);
            return;
        }
        setStep3Errors({});

        const payload = buildPayload();
        if (!payload) return;

        setSubmitting(true);
        setSubmitError(null);

        try {
            const schedule = await patientCallingService.createSchedule(payload);

            if (trigger) {
                await patientCallingService.triggerSchedule(schedule.id);
            }

            setSuccess(true);
            setTimeout(() => {
                onScheduled();
            }, 1800);
        } catch (e: any) {
            setSubmitError(e.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    }, [scheduleForm, buildPayload, onScheduled]);

    const handleSave = () => validateAndSubmit(false);
    const handleSaveAndTrigger = () => validateAndSubmit(true);

    // ─── Render ───────────────────────────────────────────────────────────────

    const footerHint =
        currentStep === 1 && !canProceedStep1
            ? 'Select a call type to continue'
            : currentStep === 2 && !canProceedStep2
            ? 'Select at least one patient record to continue'
            : null;

    const modal = (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.modalHeaderLeft}>
                        <h2 className={styles.modalTitle}>Schedule Call Campaign</h2>
                        <p className={styles.modalSubtitle}>
                            {selectedCallType && currentStep > 1
                                ? `${CALL_TYPE_LABELS[selectedCallType]} · ${selectedRecords.length} patient${selectedRecords.length !== 1 ? 's' : ''} selected`
                                : 'Set up a new call campaign in 3 steps'}
                        </p>
                    </div>
                    <button
                        type="button"
                        className={styles.closeModalBtn}
                        onClick={onClose}
                        disabled={submitting}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Stepper */}
                <div className={styles.stepper}>
                    {STEPS.map(({ step, label }) => {
                        const isDone = currentStep > step;
                        const isActive = currentStep === step;
                        return (
                            <React.Fragment key={step}>
                                <div
                                    className={`${styles.stepperItem} ${isActive ? styles.stepperItemActive : ''} ${isDone ? styles.stepperItemDone : ''}`}
                                >
                                    <div className={styles.stepperDot}>
                                        {isDone ? <CheckCircle2 size={14} /> : step}
                                    </div>
                                    <span className={styles.stepperLabel}>{label}</span>
                                </div>
                                {step < 3 && (
                                    <div className={`${styles.stepperConnector} ${isDone ? styles.stepperConnectorDone : ''}`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Body */}
                <div className={styles.modalBody}>
                    {success ? (
                        <div className={styles.successState}>
                            <CheckCircle2 size={52} className={styles.successIcon} />
                            <h3>Campaign scheduled!</h3>
                            <p>Your call campaign has been created successfully.</p>
                        </div>
                    ) : (
                        <>
                            {currentStep === 1 && (
                                <Step1CallType
                                    selected={selectedCallType}
                                    onChange={handleCallTypeChange}
                                />
                            )}

                            {currentStep === 2 && selectedCallType && (
                                <Step2RecordSelection
                                    callType={selectedCallType}
                                    selected={selectedRecords}
                                    onChange={setSelectedRecords}
                                />
                            )}

                            {currentStep === 3 && selectedCallType && scheduleForm && (
                                <Step3ScheduleConfig
                                    callType={selectedCallType}
                                    selectedCount={selectedRecords.length}
                                    formState={scheduleForm}
                                    onChange={form => {
                                        setScheduleForm(form);
                                        // Clear field errors on change
                                        setStep3Errors({});
                                    }}
                                    errors={step3Errors}
                                />
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                {!success && (
                    <div className={styles.modalFooter}>
                        <div className={styles.footerLeft}>
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    className={styles.backBtn}
                                    onClick={goBack}
                                    disabled={submitting}
                                >
                                    <ChevronLeft size={15} /> Back
                                </button>
                            )}
                        </div>

                        {footerHint && (
                            <div className={styles.footerHint}>
                                <Info size={13} />
                                <span>{footerHint}</span>
                            </div>
                        )}

                        <div className={styles.footerRight}>
                            {submitError && (
                                <span className={styles.submitError}>{submitError}</span>
                            )}

                            {currentStep < 3 ? (
                                <button
                                    type="button"
                                    className={styles.nextBtn}
                                    onClick={goNext}
                                    disabled={
                                        (currentStep === 1 && !canProceedStep1) ||
                                        (currentStep === 2 && !canProceedStep2)
                                    }
                                >
                                    Next <ChevronRight size={15} />
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className={styles.saveBtn}
                                        onClick={handleSave}
                                        disabled={submitting}
                                    >
                                        <Save size={14} />
                                        {submitting ? 'Saving…' : 'Save Schedule'}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.triggerBtn}
                                        onClick={handleSaveAndTrigger}
                                        disabled={submitting}
                                    >
                                        <Play size={14} />
                                        {submitting ? 'Working…' : 'Save & Trigger Now'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return createPortal(modal, document.body);
};

export default ScheduleCampaignFlow;